import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import type { ExpenseCategory } from '@pflegenavi/frontend/api-nursing-home';
import {
  EXPENSE_CATEGORIES,
  useCreateExpense,
} from '@pflegenavi/frontend/api-nursing-home';
import { useSnackbar } from 'notistack';
import { Typography, useErrorSnackbar } from '@pflegenavi/web-components';
import { format } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  UploadReceiptCard,
  useUploadReceipt,
} from '@pflegenavi/transaction-components';

interface FormValues {
  receiptImageIds: string[];
  receipt_not_available: boolean;
  receiptDate: Date | null;
  receiptNumber: string;
  category: ExpenseCategory | '';
  comment: string;
}

export interface AssignToExpenseManagementModalProps {
  open: boolean;
  onClose: () => void;
  /** Cash transaction group to assign — caller must ensure amount_linked === 0 */
  cashTransactionGroupId: string;
  /** Absolute group amount in cents */
  groupAmountCents: number;
  /** Pre-filled category (e.g. from Pliant). When provided, the field is locked. */
  lockedCategory?: ExpenseCategory | null;
  description?: React.ReactNode;
  onSuccess?: () => void;
}

export const AssignToExpenseManagementModal: React.FC<
  AssignToExpenseManagementModalProps
> = ({
  open,
  onClose,
  cashTransactionGroupId,
  groupAmountCents,
  lockedCategory = null,
  description,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { mutateAsync: createExpense } = useCreateExpense();

  const validationSchema = Yup.object({
    receiptImageIds: Yup.array().of(Yup.string().required()),
    receipt_not_available: Yup.boolean().required(),
    receiptDate: Yup.date().required(),
    receiptNumber: Yup.string(),
    category: Yup.string(),
    comment: Yup.string(),
  });

  const initialValues = useMemo<FormValues>(
    () => ({
      receiptImageIds: [],
      receipt_not_available: false,
      receiptDate: new Date(),
      receiptNumber: '',
      category: lockedCategory ?? '',
      comment: '',
    }),
    // Reset only when the locked category changes (e.g. switching the target row)
    // — avoid re-creating `new Date()` on every render which would loop with
    // enableReinitialize.
    [lockedCategory]
  );

  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting }) => {
      if (!values.receiptDate) {
        return;
      }
      try {
        await createExpense({
          body: {
            cash_list_transaction_group_id: cashTransactionGroupId,
            expense_parts: [
              { amount: groupAmountCents, expense_part_sequence_nr: 1 },
            ],
            // TODO(ENG-1952 frontend): submit subcategory_id on each expense_part instead of a top-level category
            comment:
              values.comment.trim() === '' ? null : values.comment.trim(),
            receipt_date: format(values.receiptDate, 'yyyy-MM-dd'),
            receipt_number:
              values.receiptNumber.trim() === ''
                ? null
                : values.receiptNumber.trim(),
            receipt_image_ids: values.receiptImageIds,
            receipt_not_available: values.receipt_not_available,
          },
        });

        enqueueSnackbar(
          t('receipts.unassociatedPliant.assignExpense.success'),
          { variant: 'success' }
        );
        onSuccess?.();
        onClose();
      } catch (error) {
        enqueueErrorSnackbar(error, t('errors.something-went-wrong'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const uploadReceipt = useUploadReceipt({ formik });

  const categoryLocked = lockedCategory !== null;
  const canConfirm =
    formik.values.receiptDate !== null &&
    !formik.isSubmitting &&
    (formik.values.receipt_not_available ||
      formik.values.receiptImageIds.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('receipts.unassociatedPliant.assignExpense.title')}
      </DialogTitle>
      <DialogContent dividers>
        {description && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}

        <Stack spacing={2}>
          <DatePicker
            label={t('receipts.unassociatedPliant.assignExpense.receiptDate')}
            value={formik.values.receiptDate}
            onChange={(value) =>
              formik.setFieldValue('receiptDate', value ?? null)
            }
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
              },
            }}
          />

          <TextField
            label={t('receipts.unassociatedPliant.assignExpense.receiptNumber')}
            value={formik.values.receiptNumber}
            onChange={(e) =>
              formik.setFieldValue('receiptNumber', e.target.value)
            }
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="expense-category-select-label">
              {t('receipts.unassociatedPliant.assignExpense.category')}
            </InputLabel>
            <Select
              labelId="expense-category-select-label"
              id="expense-category-select"
              value={formik.values.category}
              label={t('receipts.unassociatedPliant.assignExpense.category')}
              disabled={categoryLocked}
              onChange={(e) =>
                formik.setFieldValue(
                  'category',
                  e.target.value as ExpenseCategory | ''
                )
              }
            >
              {EXPENSE_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(`accounting.expenses.category.${c}`)}
                </MenuItem>
              ))}
            </Select>
            {categoryLocked && (
              <Typography variant="caption" color="grey.600" sx={{ mt: 0.5 }}>
                {t(
                  'receipts.unassociatedPliant.assignExpense.categoryLockedByPliant'
                )}
              </Typography>
            )}
          </FormControl>

          <UploadReceiptCard
            formik={formik}
            uploadReceipt={uploadReceipt}
            disabled={false}
            allowWithoutImage
          />

          <TextField
            label={t('receipts.unassociatedPliant.assignExpense.comment')}
            value={formik.values.comment}
            onChange={(e) => formik.setFieldValue('comment', e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={formik.isSubmitting}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          color="primary"
          variant="contained"
          disabled={!canConfirm}
        >
          {t('receipts.unassociatedPliant.assignExpense.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
